import React from 'react';
import moment from 'moment-timezone';
import CollapsableContent from '../../CollapsableContent';
import { RegistrationItem } from '../../../../models/registrations/registrations';
import { formatCurrencyValue } from '../../../../utils/currency'
import StandardItemPanel from '../../../inputs/registrationItems/StandardItemPanel';
import DonationItemPanel from '../../../inputs/registrationItems/DonationItemPanel';
import { LineItem } from '../../../LineItems/LineItem';
import { addToCart } from '../../../../utils/kit/modules/shoppingCart';
import { useNavigate } from 'react-router';
import ShirtItemPanel from '../../../inputs/registrationItems/ShirtItemPanel';
import { DonationConfiguration, ForSaleConfiguration, LineItemTypes, ShirtConfiguration } from '../../../LineItems/LineItemTypes/LineItemTypes';
import { chooseYourOwnPriceConfiguration } from '../../../LineItems/UnitPriceTypes/LineItemUnitPriceTypes';
import { GACntxt } from '../../../../AllianceApp';
import LoginButton from '../../../buttons/loginButton';
import { UserDetails } from '../../../../models/user/userInfo';
import { reserveItemFromInventory } from '../../../../functions/Items';
import { storeCheckoutEvent } from '../../../../functions/CheckoutEvent';


interface CollapsibleRegistrationProps {
    startingIsCollapsed: boolean,
    registeredContent ?: string,
    unRegisteredContent ?: string,
    registrationItems ?: RegistrationItem[],
    registrantList: UserDetails[],
    eventName: string,
    eventId: number
}

const CollapsibleRegistration = (props: CollapsibleRegistrationProps) => {

    const navigate = useNavigate();
    const userContext = React.useContext(GACntxt);

    const [grandTotal, setGrandTotal] = React.useState<number>(0);
    const [displayError, setDisplayError] = React.useState<boolean>(false);
    const [shirtError, setShirtError] = React.useState<boolean>(false);
    const [cartMap, setCartMap] = React.useState<Record<number, any>>({});
    const [emptyCart, setEmptyCart] = React.useState<boolean>(true);

    const isLoggedIn = userContext.userTGAId !== undefined;


    const currency = {
            prefix: '$',
            suffix: '',
            decimalSeparator: '.',
            groupSeparator: ',',
            decimalLimit: 2
    }



    const adjustGrandTotal = (adjust: number) => {
        setGrandTotal(grandTotal+adjust);
    }



    const adjustCartItem = (id: number, cart: LineItem) => 
    {
        setCartMap((prevMap) => 
        {
            return {...prevMap, [id]: cart}
        });
    }



    const grandTotalFormatted = formatCurrencyValue(grandTotal, currency).formatted;

    const currentTime = moment();



    const registrationItemContent = (regItems: RegistrationItem[], disableInputs: boolean) => {
        return regItems.map((regItem) => {
            if(regItem.itemType == 'ForSale')
            {
                return <StandardItemPanel
                registrationItem = {regItem}
                currentTime = {currentTime}
                adjustGrandTotal = {adjustGrandTotal}
                adjustCartItem = {adjustCartItem}
                disableInputs = {disableInputs}
                />
            }
            if(regItem.itemType == 'Shirts')
            {
                return <ShirtItemPanel
                registrationItem = {regItem}
                currentTime = {currentTime}
                adjustGrandTotal = {adjustGrandTotal}
                adjustCartItem = {adjustCartItem}
                disableInputs = {disableInputs}
                />
            }
            if(regItem.itemType == 'Donation')
            {
                return <DonationItemPanel
                registrationItem = {regItem}
                currentTime = {currentTime}
                adjustGrandTotal = {adjustGrandTotal}
                donationMinimum = {2.50}
                setDisplayError = {setDisplayError}
                adjustCartItem = {adjustCartItem}
                disableInputs = {disableInputs}
            />
            }
            return <div></div>
        })
    }



    const onRegisterButton = async () =>
    {
        const cartMapCopy = cartMap;

        //Reserve items

        try
        {

            const res = await Promise.allSettled(Object.entries(cartMapCopy).map(async ([key, item]) =>
            {
                if(item.itemFormat === LineItemTypes.ForSale)
                {
                    console.log(item.itemConfiguration.quantity);
                    const resp = await reserveItemFromInventory(item.itemConfiguration.dbId, userContext.userTGAId ?? 0, 
                        item.itemConfiguration.quantity);

                    console.log(resp);
                    if(resp.reserved_quantity !== item.itemConfiguration.quantity)
                    {
                        //Undo reservation
                        await reserveItemFromInventory(item.itemConfiguration.dbId, userContext.userTGAId ?? 0, 0);
                        //refresh page
                        window.location.reload();
                        //We shouldn't get this far but just in case
                        return false
                    }
                    else
                    {
                        cartMapCopy[parseInt(key)] =
                        {
                            ...cartMapCopy[parseInt(key)],
                            reservation_time: resp.timer_start_dt
                        }
                    }
                }
                return true
            }));

            if (res.some(r => r.status === 'rejected')) 
            {
            console.warn("One or more reservations failed. Reloading...");
            window.location.reload();
            }

            Object.values(cartMapCopy).map((item) => 
            {
                addToCart(item);
            })
            storeCheckoutEvent(props.eventId);
            navigate('/checkout');
        }
        catch
        {
            console.log('FAILURE!');
        }

    }

    //Temporary Effect to handle cart validation
    React.useEffect(() =>
    {
        const badgeOrdered = Object.values(cartMap).some((item) =>
        {
            if (item.itemFormat != LineItemTypes.ForSale)
            {
                return false;
            }

            return (item.itemConfiguration as ForSaleConfiguration).quantity != 0;
        })

        const shirtOrdered = Object.values(cartMap).some((item) =>
        {
            if (item.itemFormat != LineItemTypes.Shirts)
            {
                return false;
            }

            return (item.itemConfiguration as ShirtConfiguration).totalQuantity != 0;
        })

        const donationOrdered = Object.values(cartMap).some((item) =>
        {
            if (item.itemFormat != LineItemTypes.Donation)
            {
                return false;
            }

            return (item.unitPriceConfiguration as chooseYourOwnPriceConfiguration).defaultPrice.value.float != 0;
        })

        if (shirtOrdered && !badgeOrdered)
        {
            setShirtError(true);
        }
        else
        {
            setShirtError(false);
        }
        if (!badgeOrdered && !shirtOrdered && !donationOrdered)
        {
            setEmptyCart(true);
        }
        else
        {
            setEmptyCart(false);
        }
    },[cartMap])

    if(!isLoggedIn)
    {
        return(
            <CollapsableContent title={"Registration"} startingIsCollapsed={props.startingIsCollapsed}>
                <div className="notLoggedInPrompt">
                    <p>Please log in to register to this event</p>
                    <div className="notLoggedInPromptButton">
                        <LoginButton></LoginButton>
                    </div>
                </div>
                {props.registrationItems && registrationItemContent(props.registrationItems, true)}
            </CollapsableContent>
        );
    }
    console.log(userContext.userTGAId);
    console.log(props.registrantList.filter((usr) => {return usr.id === userContext.userTGAId}));


    if(props.registrantList.filter((usr) => {return usr.id === userContext.userTGAId}).length !== 0)
    {
        return(<CollapsableContent title={"Registration"} startingIsCollapsed={props.startingIsCollapsed}>
            <p>You are registered for {props.eventName}. If you would like to add a shirt to your registration, please contact us.</p>
        </CollapsableContent>)
    }


    
    return(
        <CollapsableContent title={"Registration"} startingIsCollapsed={props.startingIsCollapsed}>
            {props.registrationItems && registrationItemContent(props.registrationItems, false)}
            <br></br>
            <div className='colPanel-grandTotal'>
                <div>
                    <b>GRAND TOTAL: {grandTotalFormatted}</b>
                    {displayError &&  <p className='donationPanelTextError'>Donation below minimum of $2.50</p>}
                    {shirtError &&  <p className='donationPanelTextError'>Must purchase a badge in order to purchase shirts.</p>}
                </div>
                <div className="colPanel-grandTotalButton">
                    <button className='buttonPrimary' disabled={displayError || shirtError || emptyCart} onClick={onRegisterButton}>Register</button>
                </div>
            </div>
        </CollapsableContent>
    );
}





export default CollapsibleRegistration;